import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Phone, Video, CheckCircle2, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      nuovo: 'Nuovo',
      telefonata_target: 'Telefonata Target',
      videochiamata: 'Videochiamata',
      convertito: 'Convertito',
      perso: 'Perso'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      nuovo: 'bg-blue-50 text-blue-700 border-blue-200',
      telefonata_target: 'bg-amber-50 text-amber-700 border-amber-200',
      videochiamata: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      convertito: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      perso: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Dashboard</h1>
        <p className="text-slate-600">Panoramica dei tuoi lead e delle performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="col-span-1 md:col-span-2 bg-indigo-600 text-white border-0 shadow-lg p-6" data-testid="hero-stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-indigo-100 text-sm font-medium uppercase tracking-wide mb-2">Lead Totali</p>
              <h2 className="text-5xl font-bold mb-3">{stats?.total_leads || 0}</h2>
              <div className="flex items-center gap-2 text-indigo-100">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-medium">Tasso di conversione: {stats?.conversion_rate || 0}%</span>
              </div>
            </div>
            <Users className="h-12 w-12 text-indigo-200" />
          </div>
        </Card>

        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="new-leads-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">Nuovi Lead</p>
          <h3 className="text-3xl font-bold text-slate-900">{stats?.new_leads || 0}</h3>
        </Card>

        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="in-progress-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-amber-50 rounded-lg">
              <Phone className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">In Lavorazione</p>
          <h3 className="text-3xl font-bold text-slate-900">{stats?.in_progress || 0}</h3>
        </Card>

        <Card className="bg-white border border-slate-200 p-6 shadow-sm" data-testid="converted-card">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">Convertiti</p>
          <h3 className="text-3xl font-bold text-slate-900">{stats?.converted || 0}</h3>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="recent-leads-card">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Lead Recenti</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {recentLeads.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nessun lead disponibile. Inizia importando i tuoi primi lead!
            </div>
          ) : (
            recentLeads.map((lead) => (
              <div key={lead.id} className="p-6 hover:bg-slate-50 transition-colors" data-testid="lead-item">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">{lead.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span>{lead.email}</span>
                      <span>{lead.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusColor(lead.status)}`}>
                      {getStatusLabel(lead.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}